import { z } from "zod";

const dateTimeSchema = z.string().datetime({ offset: true });
const nullableDateTimeSchema = dateTimeSchema.nullable();

export const conversationHistoryActivityStatusSchema = z.enum([
  "complete",
  "dot",
  "progress",
  "question",
]);

export const conversationHistoryItemWireSchema = z
  .object({
    activity_status: conversationHistoryActivityStatusSchema,
    archived_at: nullableDateTimeSchema,
    id: z.string().min(1),
    is_archived: z.boolean(),
    is_pinned: z.boolean(),
    last_message_at: nullableDateTimeSchema,
    message_count: z.number().int().nonnegative(),
    organization_id: z.string().min(1),
    shop_binding_id: z.string().min(1).nullish().transform((value) => value ?? null),
    pinned_at: nullableDateTimeSchema,
    preview: z.string().max(240),
    revision: z.number().int().nonnegative(),
    title: z.string().trim().min(1).max(120),
    updated_at: dateTimeSchema,
  })
  .strict()
  .superRefine((item, context) => {
    if (item.is_pinned !== (item.pinned_at !== null)) {
      context.addIssue({
        code: "custom",
        message: "置顶状态与时间不一致",
        path: ["is_pinned"],
      });
    }
    if (item.is_archived !== (item.archived_at !== null)) {
      context.addIssue({
        code: "custom",
        message: "归档状态与时间不一致",
        path: ["is_archived"],
      });
    }
    if (item.is_archived && item.is_pinned) {
      context.addIssue({
        code: "custom",
        message: "归档会话不能保持置顶",
        path: ["is_pinned"],
      });
    }
  });

function toConversationHistoryItem(
  input: z.infer<typeof conversationHistoryItemWireSchema>,
) {
  return {
    activityStatus: input.activity_status,
    archivedAt: input.archived_at,
    id: input.id,
    isArchived: input.is_archived,
    isPinned: input.is_pinned,
    lastMessageAt: input.last_message_at,
    messageCount: input.message_count,
    organizationId: input.organization_id,
    shopBindingId: input.shop_binding_id,
    pinnedAt: input.pinned_at,
    preview: input.preview,
    revision: input.revision,
    title: input.title,
    updatedAt: input.updated_at,
  };
}

export const conversationHistoryResponseSchema = z
  .object({
    items: z.array(conversationHistoryItemWireSchema),
    list_revision: z.string().regex(/^chsnap_[a-f0-9]{32}$/u),
    next_cursor: z.string().regex(/^chcur_[a-f0-9]{32}$/u).nullable(),
    schema_version: z.literal(1),
  })
  .strict()
  .transform((response) => ({
    items: response.items.map(toConversationHistoryItem),
    listRevision: response.list_revision,
    nextCursor: response.next_cursor,
    schemaVersion: response.schema_version,
  }));

export const conversationHistoryMutationResponseSchema = z
  .object({
    item: conversationHistoryItemWireSchema,
    schema_version: z.literal(1),
  })
  .strict()
  .transform((response) => ({
    item: toConversationHistoryItem(response.item),
    schemaVersion: response.schema_version,
  }));

export type ConversationHistoryItem = ReturnType<
  typeof toConversationHistoryItem
>;
export type ConversationHistoryActivityStatus = z.infer<
  typeof conversationHistoryActivityStatusSchema
>;
